// src/components/form/DynamicUserForm.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import LinearProgress from "@mui/material/LinearProgress";
import { getAuth } from "firebase/auth";

import { useAuth } from "../../context/AuthContext";
import { firebaseApp } from "../../firebase/firebaseApp";

import QuestionStep from "./QuestionStep";
import GroupedQuestionStep from "./GroupedQuestionStep";
import ReviewStep from "./ReviewStep";

import { questions } from "../../utils/form/formQuestions";
import { isQuestionValid } from "../../utils/form/isQuestionValid";
import {
  buildSteps,
  filterQuestions,
  rangeDefaults,
} from "../../utils/form/formRuntime";
import { uploadMetadata } from "../../utils/firebase/uploadMetadata";
import FormNavButtons from "./FormNavButtons";
import { useTranslation } from "react-i18next";
import ErrorPopup from "../ErrorPopup";
import { validateClinicAssociation } from "../../utils/recordingMode/validateClinicAssociation";
import { clinicAssocErrorKey } from "../../utils/recordingMode/clinicAssocErrorKey";
import { usePatient } from "../../context/PatientContext";
import { normalizeId } from "../../utils/patient/patientStoragePath";

/**
 * @typedef {"default"|"patientEdit"} DynamicUserFormMode
 * `default` runs the onboarding wizard with review and submit.
 * `patientEdit` lets an authenticated user edit patient metadata in place.
 */

/**
 * @typedef {object} DynamicUserFormProps
 * @property {Object<string, *>} [initialData]
 *   Seed values used to prefill the form, usually from cached metadata.
 * @property {DynamicUserFormMode} [mode="default"]
 *   Determines whether the form runs as first-time onboarding or edit mode.
 */

/**
 * @typedef {object} IdConstraintResult
 * @property {boolean} enforce
 *   Whether the current flow should enforce the 7-digit patient ID rule.
 * @property {boolean} ok
 *   Whether the current patient ID value satisfies the rule.
 * @property {string} pid
 *   Normalized patient ID value used for validation and persistence.
 */

/**
 * Multi-step demographics and patient-metadata form used before recordings.
 *
 * The component drives both:
 * - first-run onboarding before measurement capture; and
 * - subsequent patient/profile edits from the authenticated app shell.
 *
 * It filters questions dynamically, groups them into wizard steps, validates
 * required fields, persists metadata to Firebase Storage, and updates the
 * active patient context used elsewhere in the app.
 *
 * @param {DynamicUserFormProps} props
 * @returns {JSX.Element}
 */
function DynamicUserForm({ initialData = {}, mode = "default" }) {
  const { t } = useTranslation();

  const { user } = useAuth();
  const navigate = useNavigate();

  const { setActiveId } = usePatient();

  const isPatientEdit = mode === "patientEdit";

  const [popup, setPopup] = useState(null);
  const closePopup = useCallback(() => setPopup(null), []);

  const [formData, setFormData] = useState(initialData);
  const [currentStep, setCurrentStep] = useState(0);

  const filtered = useMemo(
    () => filterQuestions(questions, formData, user),
    [formData, user],
  );

  const steps = useMemo(() => {
    // PatientEdit flow: NO review step
    if (isPatientEdit) return buildSteps(filtered, { includeReview: false });
    return buildSteps(filtered);
  }, [filtered, isPatientEdit]);

  const totalSteps = steps.length;
  const progress = totalSteps ? (currentStep / totalSteps) * 100 : 0;

  useEffect(() => {
    const defaults = rangeDefaults(filtered, formData);
    if (Object.keys(defaults).length) {
      setFormData((p) => ({ ...p, ...defaults }));
    }
  }, [filtered]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  /** True when every required field in the current step is valid. */
  const validateStep = () => {
    const step = steps[currentStep];
    if (!step) return true;

    if (step.type === "single") return isQuestionValid(step.question, formData);
    if (step.type === "group")
      return !step.questions.some((q) => !isQuestionValid(q, formData));
    return true; // review step (default mode only)
  };

  /**
   * Determines whether we should enforce the 7-digit patient id constraint.
   *
   * Policy:
   * - If the id question is required => enforce always.
   * - If not required => enforce only when user has entered a non-empty value.
   *
   * @returns {IdConstraintResult}
   */
  const idConstraint = useMemo(() => {
    const idQ = filtered.find((q) => q.id === "id") || null;
    const pid = String(formData.id ?? "").trim();

    const isRequired = !!idQ?.required;
    const hasValue = pid.length > 0;

    const enforce = isRequired || hasValue;
    const ok = !enforce || /^\d{7}$/.test(pid);

    return { enforce, ok, pid };
  }, [filtered, formData.id]);

  /**
   * Persist the form (used by:
   * - default mode on final step
   * - patientEdit mode "Save" button)
   */
  const persist = useCallback(async () => {
    const { currentUser } = getAuth(firebaseApp);
    if (!currentUser) throw new Error("No authenticated user");

    const id = String(formData.id ?? "").trim();
    const clinicStudyId = String(formData.clinicStudyId ?? "").trim();

    if (clinicStudyId) {
      const res = await validateClinicAssociation({ clinicStudyId, id });
      if (!res.ok) {
        const msgKey = clinicAssocErrorKey(res.reason);
        setPopup({
          message:
            res.reason === "not_found"
              ? t(msgKey, { clinic: clinicStudyId })
              : t(msgKey),
        });
        return false;
      }
    }

    await uploadMetadata(currentUser.uid, formData);
    const pid = normalizeId(formData.id ?? formData.id);
    setActiveId(pid);
    return true;
  }, [formData, t]);

  const handleNext = useCallback(
    async (e) => {
      e?.preventDefault();

      const isLast = currentStep === totalSteps - 1;

      // default mode: final step persists
      if (!isPatientEdit && isLast) {
        try {
          const ok = await persist();
          if (!ok) return;

          try {
            sessionStorage.removeItem("formMetadata");
          } catch {}

          navigate("/recording", { replace: true });
          return;
        } catch (err) {
          console.error("Metadata upload failed:", err);
        }
      }

      if (!validateStep()) {
        setPopup({ message: t("form.reqFieldsAlert") });
        return;
      }

      setCurrentStep((s) => Math.min(totalSteps - 1, s + 1));
    },
    [
      currentStep,
      totalSteps,
      isPatientEdit,
      persist,
      navigate,
      t,
      validateStep,
    ],
  );

  const handlePrev = (e) => {
    e.preventDefault();
    setCurrentStep((s) => Math.max(0, s - 1));
  };

  // patientEdit: Save should validate ALL steps, not just current step
  const handleSave = useCallback(
    async (e) => {
      e?.preventDefault();

      if (!idConstraint.ok) {
        setPopup({
          message: t(
            "id.hint7digits",
            "Enter a 7-digit Patient ID (numbers only).",
          ),
        });
        return;
      }

      // Validate every step
      for (let i = 0; i < steps.length; i += 1) {
        const step = steps[i];
        if (!step) continue;

        let ok = true;
        if (step.type === "single")
          ok = isQuestionValid(step.question, formData);
        else if (step.type === "group") {
          ok = !step.questions.some((q) => !isQuestionValid(q, formData));
        }

        if (!ok) {
          setPopup({ message: t("form.reqFieldsAlert") });
          setCurrentStep(i); // jump user to first invalid step
          return;
        }
      }

      try {
        const ok = await persist();
        if (!ok) return;

        // patientEdit: return to profile (or wherever you want)
        navigate("/profile", { replace: true });
      } catch (err) {
        console.error("Metadata upload failed:", err);
      }
    },
    [steps, formData, persist, navigate, t],
  );

  useEffect(() => {
    if (!filtered.length) {
      navigate(isPatientEdit ? "/profile" : "/recording");
    }
  }, [filtered, navigate, isPatientEdit]);

  const translationForm = "form";

  const renderStep = () => {
    const step = steps[currentStep];
    if (!step) return null;

    switch (step.type) {
      case "single":
        return (
          <QuestionStep
            question={step.question}
            value={formData[step.question.id]}
            onChange={handleChange}
            autoFocus
            onEnter={isPatientEdit ? undefined : handleNext}
            translationForm={translationForm}
          />
        );
      case "group":
        return (
          <GroupedQuestionStep
            questions={step.questions}
            formData={formData}
            onChange={handleChange}
            onEnter={isPatientEdit ? undefined : handleNext}
            translationForm={translationForm}
          />
        );
      default:
        return (
          <ReviewStep
            questions={filtered}
            formData={formData}
            translationForm={translationForm}
          />
        );
    }
  };

  return (
    <div className="user-form p-4 pt-0 w-full mx-auto flex flex-col justify-between gap-4">
      <ErrorPopup popup={popup} onClose={closePopup} />

      {/* a) patientEdit: do NOT show linear progress */}
      {!isPatientEdit ? (
        <LinearProgress
          variant="determinate"
          value={progress}
          className="rounded-full"
          sx={{
            backgroundColor: "#B776B9",
            "& .MuiLinearProgress-bar": { backgroundColor: "#852170" },
          }}
        />
      ) : null}

      {renderStep()}

      {/* c) patientEdit: full-width Save button, no Next/Prev */}
      {isPatientEdit ? (
        <div className="flex flex-col gap-2 w-full h-fit text-center">
          <button
            type="button"
            className="primary-button w-full"
            onClick={handleSave}
          >
            {t("profile.save", { defaultValue: "Save" })}
          </button>
          <p>This screen is for collecting study-related information only.</p>
        </div>
      ) : (
        <FormNavButtons
          hasPrev={currentStep > 0}
          onPrev={handlePrev}
          onNext={handleNext}
          isLast={currentStep === totalSteps - 1}
        />
      )}
    </div>
  );
}

export default DynamicUserForm;
